import React, { useEffect, useMemo, useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button } from "@mui/material";
import { Link, useParams } from "react-router-dom";

import Header from "@/components/MealPlanForm/Header";
import MealPlanFormContainer from "@/components/MealPlanForm/MealPlanFormContainer";
import MealPlannerForm from "@/components/MealPlanForm/MealPlannerForm/MealPlannerForm";
import PlanningForm from "@/components/MealPlanForm/PlanningForm/PlanningForm";
import Step from "@/components/MealPlanForm/Step";
import MealPlanFormStore from "@/components/MealPlanForm/store/MealPlanFormStore";
import { graphql } from "@/gql";
import useAlert from "@/hooks/useAlert";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { displayError } from "@/utils/errors";

import TemplateDetailsForm, {
  TemplateDetailsFormValues,
} from "../components/TemplateDetailsForm";

const mealPlanTemplateQuery = graphql(`
  query MealPlanTemplate($id: ID!) {
    mealPlanTemplate(id: $id) {
      id
      name
      description
      durationDays
      recommendedCalories
    }
  }
`);

const updateMealPlanTemplateMutation = graphql(`
  mutation UpdateMealPlanTemplate($input: UpdateMealPlanTemplateInput!) {
    updateMealPlanTemplate(input: $input) {
      mealPlanTemplate {
        id
        name
        description
        durationDays
        recommendedCalories
      }
    }
  }
`);

const EditMealPlanTemplatePage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const snackbar = useGlobalSnackbar();
  const alert = useAlert();

  const { data } = useQuery(mealPlanTemplateQuery, {
    variables: { id: templateId ?? "" },
  });
  const detailsFormValues = useMemo(() => {
    return data?.mealPlanTemplate
      ? {
          name: data.mealPlanTemplate.name,
          description: data.mealPlanTemplate.description ?? undefined,
          durationDays: data.mealPlanTemplate.durationDays,
          recommendedCalories: data.mealPlanTemplate.recommendedCalories,
        }
      : undefined;
  }, [data]);
  const [store] = useState(new MealPlanFormStore("template"));
  useEffect(() => {
    store.loadTemplate(templateId ?? "");
  }, [templateId]);

  const [updateMealPlanTemplate] = useMutation(updateMealPlanTemplateMutation);
  async function handleDetailsSubmit(
    values: TemplateDetailsFormValues,
    isDirty: boolean
  ) {
    try {
      if (isDirty) {
        await updateMealPlanTemplate({
          variables: {
            input: {
              id: templateId ?? "",
              patch: {
                name: values.name,
                description: values.description,
                durationDays: values.durationDays,
                recommendedCalories: values.recommendedCalories,
              },
            },
          },
        });
      }
      store.setRecommendedCalories(values.recommendedCalories.toString());
      store.columns.setDuration(values.durationDays);
      store.nextStep();
    } catch (e) {
      snackbar.open(displayError(e, "Unable to save meal plan template"));
    }
  }
  async function handlePlanningSubmit() {
    // Optimistically go to the next step.
    store.nextStep();
    try {
      await updateMealPlanTemplate({
        variables: {
          input: {
            id: templateId ?? "",
            patch: {
              ...store.macronutrients.toAttributes(),
              ...store.foodExchange.toAttributes(),
            },
          },
        },
      });
    } catch (e) {
      snackbar.open(displayError(e, "Unable to save meal plan template"));
      // On error, go back to this step.
      store.setCurrentStep(1);
    }
  }

  async function save() {
    return await updateMealPlanTemplate({
      variables: {
        input: {
          id: templateId ?? "",
          patch: store.toAttributes(),
        },
      },
    });
  }

  async function handleAutoSave() {
    try {
      await save();
    } catch (e) {
      snackbar.open(displayError(e, "Unable to save meal plan template"));
    }
  }

  async function handleManualSave() {
    try {
      await save();
      alert.open("Meal plan template saved successfully");
    } catch (e) {
      snackbar.open(displayError(e, "Unable to save meal plan template"));
    }
  }

  return (
    <Box paddingY={4}>
      <Box marginBottom={4}>
        <Button
          component={Link}
          to="/templates/meals"
          variant="text"
          startIcon={<ArrowBackIcon fontSize="small" color="primary" />}
        >
          Back to templates
        </Button>
      </Box>
      <MealPlanFormContainer
        store={store}
        header={
          <Header
            title="Meal Template"
            steps={["Details", "Planning", "Meal plan"]}
          />
        }
      >
        <Step index={0}>
          <TemplateDetailsForm
            values={detailsFormValues}
            submitLabel="Next"
            onSubmit={handleDetailsSubmit}
          />
        </Step>
        <Step index={1}>
          <PlanningForm onSubmit={handlePlanningSubmit} />
        </Step>
        <Step index={2}>
          <MealPlannerForm
            backButtonTo="/templates/meals"
            backButtonLabel="Back to templates"
            onAutoSave={handleAutoSave}
            onManualSave={handleManualSave}
          />
        </Step>
      </MealPlanFormContainer>
    </Box>
  );
};

export default EditMealPlanTemplatePage;
